<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import EnergyInAction from '$lib/PageSections/IndowInAction/EnergyInAction.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';
	import TrustedChoice from '$lib/PageSections/TrustedChoice/TrustedChoice.svelte';
	import condensationImage from './images/condensation.jpg';
	import installImage from './images/install.jpg';
	import mainImage from './images/main.jpg';
</script>

<Section>
	<p>
		If your home struggles to stay warm in winter or cool in summer, and your
		energy bills keep climbing, your windows could be the culprit. Acrylic
		window inserts provide a straightforward and highly effective way to improve
		your home's comfort and efficiency.
	</p>
	<p>
		Acrylic, the material behind our <a href="/solutions/window-insulation"
			>inserts</a
		>, is a game changer in home insulation. It's not just a substitute for
		glass; it's a material specifically chosen for its exceptional insulating
		properties, lightweight durability, and clarity. In fact, the superior
		thermal and soundproofing qualities of acrylic are so well-regarded that
		it's the primary material used for airplane windows, where extreme
		temperature variations and loud engine noise are significant concerns. This
		same robust performance translates beautifully to home insulation. Here's
		how acrylic inserts can make your home more comfortable while saving you
		money.
	</p>
	<Image src={mainImage} loading="eager" />
</Section>

<Section>
	<h2>Acrylic Retains Heat, Reducing Energy Loss</h2>
	<p>
		Heat transfer occurs whenever warm air meets a colder surface. For
		traditional glass windows, this means that your warm indoor air seeps out in
		the winter and cold air seeps into your space. This makes your heating (and
		cooling) systems work overtime, driving up your energy bills.
	</p>
	<p>
		Here's where acrylic shines. Acrylic transmits heat seven times more slowly
		than typical glass, creating a thermal barrier that significantly reduces
		energy loss. During the colder months, acrylic inserts <a
			href="/solutions/insulating-drafty-windows"
			>keep the warmth indoors and the chill out</a
		>. In the summer, they
		<a href="/solutions/insulate-windows-from-heat"
			>slow the heat from entering</a
		>, helping you use your air conditioner less.
	</p>
	<p>
		By maintaining a stable indoor temperature, acrylic inserts not only create
		year-round comfort but also reduce your carbon footprint and decrease your
		household energy use an average of 20%!
	</p>
	<EnergyInAction />
</Section>
<Section>
	<h2 class="text-left md:text-center">Say Goodbye to Condensation</h2>
	<p class="text-left">
		<a href="/solutions/prevent-window-condensation">Condensation</a> forms when
		warm air inside your home meets a cold glass window that's dropped to the dew
		point. The result is moisture build-up, which can lead to unsightly water stains
		and mold on window sills.
	</p>
	<p class="text-left">
		Acrylic inserts help eliminate this problem. Because acrylic retains heat
		far better than glass, it prevents windows from cooling to the dew point.
		The result is a moisture-free surface, leaving your windows clear and your
		home healthier.
	</p>

	<Image src={condensationImage} />
</Section>
<Section backgroundColor="grey-light">
	<h2 class="text-indow-grey-dark">Lightweight and User-Friendly Design</h2>
	<p>
		Acrylic isn't just a superior insulator; it's also incredibly practical.
		It's lighter than glass, making inserts easy to handle during installation
		and removal. Plus, acrylic is durable and resists shattering, ensuring
		long-term performance and safety. Even better, these inserts blend
		seamlessly into your existing window frames, preserving a polished aesthetic
		while improving functionality. You'll enjoy an exceptionally clear and
		unobstructed view, maintaining the beauty of your surroundings.
	</p>
</Section>

<Section>
	<h2>Insulation Made Simple</h2>
	<p>
		Installing acrylic window inserts is one of the most efficient ways to
		insulate your home. They're designed to work alongside your existing windows
		rather than replace them, creating an airtight seal that blocks drafts and
		regulates temperature. This means you don't need an expensive <a
			href="/resources/storm-windows-guide/replacement">window replacement</a
		> project to achieve significant energy savings and comfort improvements.
	</p>
	<Image src={installImage} />
</Section>
<TrustedChoice />
<Reviews />
<GetAFreeEstimate />
